/* eslint @typescript-eslint/no-explicit-any: 0 */

import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UtilService } from '@com-delta-idp/mnx-oauth';
import { AppConfigService } from '@com-delta-idp/mnx-utils';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  CACHE_HEADER,
  EndpointKeys,
  EXCEPTION_HEADER_SUBDOMAINS,
  HTTP_HEADERS,
  IDP_APP,
  POST_CACHEABLE,
  SUBDOMAINS_INCLUDED,
  TRANCSACTION_ID,
  TRANSACTION_ID_EXCLUDE
} from '../../constants/cache-header.constants';
import { RequestCache } from '../trips-request-cache/trips-request-cache';

/**
 * If request has header 'x-fr-cache' is set (e.g., aem data) then
 * response is cached and returned  as observable.
 * If not in cache or not cacheable,
 * pass request through to next()
 */
@Injectable()
export class CachingInterceptor implements HttpInterceptor {
  constructor(
    private cache: RequestCache,
    private utils: UtilService,
    private appConfig: AppConfigService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    req = this.addTransactionId(req);
    req = this.addExceptionHeader(req);
    req = this.addAdditionalHeaders(req);
    // continue if not cacheable.
    if (this.isCacheable(req) || this.isPostCachable(req)) {
      const cachedResponse = this.cache.get(req);
      // cache-or-fetch
      return cachedResponse ? of(cachedResponse) : this.sendRequest(req, next);
    } else {
      return next.handle(req);
    }
  }

  /** Is this request cacheable? */
  private isCacheable(req: HttpRequest<any>) {
    // Only GET requests are cacheable
    return req.method === 'GET' && req.headers.get(CACHE_HEADER);
  }

  /**
   * Get server response observable by sending request to `next()`.
   * Will add the response to the cache on the way out.
   */
  private sendRequest(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap((event) => {
        // There may be other events besides the response.
        if (event instanceof HttpResponse) {
          this.cache.put(req, event); // Update the cache.
        }
      })
    );
  }

  addTransactionId(req: HttpRequest<any>): HttpRequest<any> {
    if (TRANSACTION_ID_EXCLUDE.some((api) => req.url.includes(api))) {
      return req;
    }
    const currentId = req.headers.get(TRANCSACTION_ID);
    const prefix = HTTP_HEADERS.TRANSACTION_ID_PREFIX;
    const uniqueId = this.utils.generateUniqueIdentifier();
    const id =
      req.headers.has(TRANCSACTION_ID) && !currentId?.includes(prefix)
        ? `${prefix}-${currentId}-${uniqueId}`
        : `${prefix}-${uniqueId}`;
    return req.clone({
      headers: req.headers.set(TRANCSACTION_ID, id)
    });
  }

  addAdditionalHeaders(req: HttpRequest<any>): HttpRequest<any> {
    return SUBDOMAINS_INCLUDED.find((api) => req.url.includes(api))
      ? req.clone({
          withCredentials: true
        })
      : req;
  }

  addExceptionHeader(req: HttpRequest<any>): HttpRequest<any> {
    let exceptionHeader = '';
    this.appConfig
      .getConfigValue(IDP_APP, EndpointKeys.EXCEPTION_HEADER_VALUE)
      .subscribe((value: string) => {
        exceptionHeader = value ? value : '';
      });
    return EXCEPTION_HEADER_SUBDOMAINS.find((api) => req.url.includes(api))
      ? req.clone({
          headers: req.headers.set('X-AIR4_MyTrips_Test', exceptionHeader)
        })
      : req;
  }

  isPostCachable(req: HttpRequest<any>): boolean {
    return POST_CACHEABLE.find((api) => req.url.includes(api)) ? true : false;
  }
}
